/**
 * Οθόνη τραπεζιού του ταμείου — ό,τι ανοίγει με πάτημα σε κατειλημμένο (κόκκινο) ή σε τραπέζι με
 * λογαριασμό (κίτρινο) στην κάτοψη.
 *
 * Τρία πράγματα με μια ματιά (GENERIS 6/10/2026: «μου φαίνεται άσχημη η οθόνη αυτή»):
 *  1. ποιο τραπέζι και σε τι κατάσταση (κεφαλίδα: αριθμός, ζώνη, κατάσταση, άτομα, πόση ώρα, ποιος),
 *  2. τι έχει παραγγείλει (αριστερά: παραγγελίες με πιάτα, επιλογές, «χωρίς», σχόλια),
 *  3. τι χρωστά και ποιο είναι το επόμενο βήμα (δεξιά: σύνολο/πληρωμένα/υπόλοιπο, μετά οι ενέργειες).
 * Χρώμα μόνο όπου σημαίνει κάτι: η κύρια ενέργεια, η κατάσταση, η καταστροφική ενέργεια — όχι ένα
 * χρώμα ανά κουμπί.
 *
 * ΜΟΝΟ εμφάνιση: ποσά από τη συνεδρία του server και από utils/dineInMoney (ίδια με PDA/πληρωμή),
 * κάθε ενέργεια είναι handler του DineInOperations — καμία κλήση δικτύου εδώ.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X, Users, Clock, User, Plus, MessageSquare, FileText, Move, Percent, Gift, Ban, Receipt, Check,
  CreditCard, Loader2, Utensils, Printer, Lock, CalendarDays, Star, AlertTriangle,
} from 'lucide-react';
import { allergiesOf, FloorReservation } from '../../utils/dineInReservations';
import { tablePaymentState } from '../../utils/dineInMoney';
import { itemModifiers, hasModifiers } from '../../utils/itemModifiers';
import { floorTone, formatElapsed, formatTableAmount, FloorTone } from '../../utils/dineInFloor';
import { fmtClock } from '../../utils/athensDate';
import { useTheme } from '../../contexts/ThemeContext';

export interface TableScreenItem {
  id: number;
  product_id: number;
  product_name: string;
  size_id?: number | null;
  size_name: string | null;
  quantity: number;
  total_price: number | string;
  status?: string;
  is_treat?: boolean;
  notes?: string | null;
  course_number?: number;
  selected_options?: unknown;
  removed_ingredients?: unknown;
}

export interface TableScreenOrder {
  id: number;
  order_number: number;
  status: string;
  created_at: string;
  created_by_name?: string | null;
  items: TableScreenItem[];
}

export interface TableScreenDetails {
  /** Το τραπέζι όπως το στέλνει το …/details (μαζί με το όνομα ζώνης). */
  table?: { id?: number; zone_name?: string | null } | null;
  session: {
    id: number;
    status: string;
    guest_count: number;
    opened_at: string;
    opened_by_name?: string | null;
    subtotal?: number | string | null;
    discount_amount?: number | string | null;
    total_amount: number | string;
    paid_amount?: number | string | null;
    payment_unlocked?: boolean;
    /** Προκαταβολή κράτησης (Sala): paid = θα αφαιρεθεί με την απόδειξη, applied = αφαιρέθηκε. */
    deposit?: { status: 'paid' | 'applied'; amount: number; kept: number; card: string | null } | null;
    deposit_pending?: number | null;
  } | null;
  orders: TableScreenOrder[];
  paidItems?: Array<{ order_item_id: number; quantity_paid: number; payment_type: string }>;
}

export interface TableScreenProps {
  table: { id: number; table_number: string; name?: string | null; status: string };
  /** Η κράτηση που κάθεται στο τραπέζι (ή η επόμενη, αν είναι ελεύθερο): όνομα, VIP, αλλεργίες. */
  reservation?: FloorReservation | null;
  details: TableScreenDetails;
  coursesEnabled: boolean;
  guestsEnabled: boolean;
  billPrintEnabled: boolean;
  kitchenMessagesEnabled: boolean;
  /** hasPermission του ταμείου */
  can: (permission: string) => boolean;
  /** Μετά το παραστατικό: μόνο πληρωμή (ο server απορρίπτει τα υπόλοιπα). */
  sessionLocked: boolean;
  lockReason: string | null;
  /** Σύνολο 0 € (όλα κερασμένα / έκπτωση 100%): μόνο κλείσιμο. */
  zeroTotal: boolean;
  busy: { cancel: boolean; bill: boolean; receipt: boolean; closeZero: boolean };
  /** Κανόνας εμφάνισης μεγέθους (showSizeName, απόφαση 15). */
  sizeNameVisible: (item: TableScreenItem) => boolean;
  onClose: () => void;
  onNewOrder: () => void;
  onKitchenMessage: () => void;
  onCorrection: () => void;
  onMove: () => void;
  onDiscount: () => void;
  onTreat: () => void;
  onCancel: () => void;
  onPrintBill: () => void;
  onCloseZero: () => void;
  onIssueReceipt: () => void;
  onIssueInvoice: () => void;
  onPayment: () => void;
  onMarkServed: (orderId: number) => void;
}

const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};

// «00:15», ώρα Ελλάδας. Ως 24/10/2026 `hour12: false` με el-GR έγραφε «24:15» μετά τα μεσάνυχτα.
const time = (iso: string): string => fmtClock(iso);

/** Κατάσταση τραπεζιού στην κεφαλίδα — ίδια χρώματα με την κάτοψη (utils/dineInFloor). */
const TONE_BADGE: Record<FloorTone, string> = {
  free: 'bg-emerald-600 text-white',
  occupied: 'bg-red-600 text-white',
  bill: 'bg-yellow-400 text-slate-900',
  reserved: 'bg-blue-600 text-white',
};
const TONE_DOT: Record<FloorTone, string> = {
  free: 'bg-emerald-500', occupied: 'bg-red-500', bill: 'bg-yellow-400', reserved: 'bg-blue-500',
};
/** Νέα εμφάνιση: ίδια λογική με την κάτοψη — απαλό φόντο + χρωματιστό περίγραμμα. */
const TONE_BADGE_V2: Record<FloorTone, string> = {
  free: 'bg-surface-2 border-2 border-emerald-500/60 text-t1',
  occupied: 'bg-red-50 border-2 border-red-500 text-red-800 dark:bg-red-500/15 dark:border-red-400 dark:text-red-100',
  bill: 'bg-amber-50 border-2 border-amber-500 text-amber-900 dark:bg-amber-400/15 dark:border-amber-400 dark:text-amber-100',
  reserved: 'bg-brand-soft border-2 border-brand text-t1',
};
const TONE_DOT_V2: Record<FloorTone, string> = {
  free: 'bg-emerald-500', occupied: 'bg-red-500', bill: 'bg-amber-500', reserved: 'bg-brand',
};
/** Νέα εμφάνιση: ΕΝΑ χρώμα για την κύρια ενέργεια κάθε φορά. */
const PRIMARY_V2 = 'bg-brand text-brand-fg hover:brightness-110';

const ORDER_STATUS: Record<string, { label: string; tone: string }> = {
  pending: { label: 'Αναμονή', tone: 'bg-surface-3 text-t2' },
  sent_to_kitchen: { label: 'Στην κουζίνα', tone: 'bg-blue-500/15 text-blue-700 dark:text-blue-300' },
  preparing: { label: 'Ετοιμάζεται', tone: 'bg-amber-500/15 text-amber-800 dark:text-amber-300' },
  ready: { label: 'Έτοιμη', tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  served: { label: 'Σερβιρίστηκε', tone: 'bg-surface-3 text-t3' },
  cancelled: { label: 'Ακυρωμένη', tone: 'bg-red-500/15 text-red-700 dark:text-red-300' },
};

const COURSE_LABELS = ['Πρώτα', 'Δεύτερα', 'Τρίτα', 'Τέταρτα', 'Πέμπτα'];

/** Δευτερεύουσα ενέργεια: ουδέτερο κουμπί, το χρώμα μόνο στο εικονίδιο για αναγνώριση. */
const SideAction: React.FC<{
  icon: React.ElementType;
  iconClass: string;
  label: string;
  onClick: () => void;
}> = ({ icon: Icon, iconClass, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="min-h-[48px] px-3 rounded-xl bg-surface-1 border border-line-strong hover:bg-surface-3 active:scale-[0.98] transition-all flex items-center gap-2.5 text-left"
  >
    <Icon className={`w-5 h-5 shrink-0 ${iconClass}`} />
    <span className="text-sm font-semibold text-t1 leading-tight">{label}</span>
  </button>
);

const TableScreen: React.FC<TableScreenProps> = ({
  table, reservation, details, coursesEnabled, guestsEnabled, billPrintEnabled, kitchenMessagesEnabled, can,
  sessionLocked, lockReason, zeroTotal, busy, sizeNameVisible,
  onClose, onNewOrder, onKitchenMessage, onCorrection, onMove, onDiscount, onTreat, onCancel,
  onPrintBill, onCloseZero, onIssueReceipt, onIssueInvoice, onPayment, onMarkServed,
}) => {
  const { t } = useTranslation(['dineIn', 'common']);
  // «Νέα εμφάνιση» (ανά συσκευή): ίδια διάταξη, χρώμα μόνο στην κύρια ενέργεια και στην κατάσταση.
  const { look } = useTheme();
  const v2 = look === 'v2';
  const money = (amount: unknown) => formatTableAmount(amount, look);
  const session = details.session;
  const orders = details.orders || [];
  const tone = floorTone(table.status);

  // Ο χρόνος του τραπεζιού μετρά όσο μένει ανοιχτή η οθόνη.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  const liveOrders = orders.filter((o) => o.status !== 'cancelled');
  const itemCount = liveOrders.reduce(
    (s, o) => s + (o.items || []).filter((i) => i.status !== 'cancelled').reduce((n, i) => n + (num(i.quantity) || 1), 0),
    0
  );
  // Πληρωμένα/υπόλοιπο: η ΙΔΙΑ συνάρτηση με την οθόνη πληρωμής και το PDA.
  const payment = useMemo(() => tablePaymentState(details as any), [details]);
  const discount = num(session?.discount_amount);
  const total = session ? num(session.total_amount) : 0;
  const subtotal = session && session.subtotal != null && num(session.subtotal) > 0 ? num(session.subtotal) : total + discount;
  const receiptIssued = session?.status === 'receipt_printed' || session?.status === 'invoice_printed';
  const canCollect = !!session && !zeroTotal && can('dine_in_process_payment') &&
    (receiptIssued || (session.status === 'open' && session.payment_unlocked === true));
  const showPaid = !!session && payment.alreadyPaid > 0.005;

  const statusLabel =
    session?.status === 'receipt_printed' ? t('dineIn:tableScreen.receiptIssued') :
    session?.status === 'invoice_printed' ? t('dineIn:tableScreen.invoiceIssued') :
    tone === 'occupied' ? t('dineIn:occupied') :
    tone === 'free' ? t('dineIn:available') : '';

  const elapsed = session ? formatElapsed(session.opened_at, now) : '';

  // Δευτερεύουσες ενέργειες — ίδιοι κανόνες ορατότητας με πριν.
  const sideActions: Array<{ key: string; icon: React.ElementType; iconClass: string; label: string; onClick: () => void }> = [];
  if (kitchenMessagesEnabled && can('dine_in_create_order') && session) {
    sideActions.push({ key: 'kitchen', icon: MessageSquare, iconClass: v2 ? 'text-t3' : 'text-teal-600 dark:text-teal-400',label: t('dineIn:kitchenMessage', { defaultValue: 'Μήνυμα κουζίνας' }), onClick: onKitchenMessage });
  }
  if (can('dine_in_move_table') && session && !sessionLocked) {
    sideActions.push({ key: 'move', icon: Move, iconClass: v2 ? 'text-t3' : 'text-indigo-600 dark:text-indigo-400',label: t('dineIn:move'), onClick: onMove });
  }
  if (can('dine_in_apply_discount') && session && !sessionLocked) {
    sideActions.push({ key: 'discount', icon: Percent, iconClass: v2 ? 'text-t3' : 'text-purple-600 dark:text-purple-400',label: t('common:discount'), onClick: onDiscount });
  }
  if (can('dine_in_treat') && session && orders.length > 0 && !sessionLocked) {
    sideActions.push({ key: 'treat', icon: Gift, iconClass: v2 ? 'text-t3' : 'text-emerald-600 dark:text-emerald-400',label: t('dineIn:treat'), onClick: onTreat });
  }
  if (can('dine_in_correction_order') && session && orders.length > 0) {
    sideActions.push({ key: 'correction', icon: FileText, iconClass: v2 ? 'text-t3' : 'text-amber-600 dark:text-amber-400',label: t('dineIn:correction'), onClick: onCorrection });
  }

  const canNewOrder = can('dine_in_create_order') && !sessionLocked;
  // Μία κύρια ενέργεια κάθε φορά: με είσπραξη/κλείσιμο 0 € διαθέσιμα, η νέα παραγγελία γίνεται δευτερεύουσα.
  const newOrderIsPrimary = !(canCollect || (zeroTotal && can('dine_in_process_payment')));
  const canReceipt = !zeroTotal && can('dine_in_print_receipt') && !!session && !receiptIssued;
  const canInvoice = !zeroTotal && can('dine_in_print_invoice') && !!session && !receiptIssued;
  const canBill = billPrintEnabled && !!session && session.status === 'open';

  const renderItem = (item: TableScreenItem, dimmed: boolean) => {
    const mods = itemModifiers(item);
    const showSize = !!item.size_name && sizeNameVisible(item);
    return (
      <li key={item.id} className="flex items-start gap-3 py-2" data-testid={`table-item-${item.id}`}>
        <span className="w-9 shrink-0 text-right text-[15px] font-semibold text-t3 tabular-nums leading-snug">
          {num(item.quantity) || 1}×
        </span>
        <div className="min-w-0 flex-1">
          <p className={`text-[15px] font-semibold leading-snug break-words ${dimmed ? 'text-t3 line-through' : 'text-t1'}`}>
            {item.product_name}
            {showSize && <span className={`font-medium ${v2 ? 'text-t3' : 'text-blue-600 dark:text-blue-400'}`}> · {item.size_name}</span>}
            {item.is_treat && (
              <span className="ml-2 inline-block align-middle px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 no-underline">
                {t('dineIn:treatBadge')}
              </span>
            )}
          </p>
          {hasModifiers(mods) && (
            <div className="mt-0.5 space-y-0.5 text-[13px] leading-snug">
              {mods.options.length > 0 && (
                <p className={v2 ? 'font-medium text-t2' : 'font-semibold text-indigo-700 dark:text-indigo-300'}>{mods.options.map((o) => `• ${o}`).join('  ')}</p>
              )}
              {mods.removed.length > 0 && (
                <p className="font-semibold text-red-700 dark:text-red-300">χωρίς {mods.removed.join(', ')}</p>
              )}
              {mods.note && <p className={v2 ? 'text-t2 italic' : 'text-amber-700 dark:text-amber-400'}>📝 {mods.note}</p>}
            </div>
          )}
        </div>
        <span className={`shrink-0 text-[15px] font-semibold tabular-nums leading-snug ${dimmed || item.is_treat ? 'text-t4' : 'text-t1'}`}>
          {item.is_treat ? '—' : money(num(item.total_price))}
        </span>
      </li>
    );
  };

  const renderOrder = (order: TableScreenOrder) => {
    const cancelled = order.status === 'cancelled';
    const status = ORDER_STATUS[order.status] || { label: order.status, tone: 'bg-surface-3 text-t2' };
    const items = order.items || [];
    const courses = Array.from(new Set(items.map((i) => i.course_number || 1))).sort((a, b) => a - b);
    // Ετικέτα πιάτου μόνο όταν λέει κάτι: πάνω από ένα πιάτο, ή όλα σε πιάτο ≠ 1 (περιμένουν «ΠΑΣΟ»).
    const showCourses = coursesEnabled && (courses.length > 1 || (courses.length === 1 && courses[0] !== 1));
    return (
      <section key={order.id} className={`py-3 ${cancelled ? 'opacity-60' : ''}`} aria-label={t('dineIn:orderNumber', { number: order.order_number })}>
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-bold text-t1">{t('dineIn:orderNumber', { number: order.order_number })}</h3>
          <span className="text-sm text-t3 tabular-nums">{time(order.created_at)}</span>
          {order.created_by_name && <span className="text-sm text-t3 truncate">· {order.created_by_name}</span>}
          <span className={`ml-auto px-2.5 py-0.5 rounded-full text-xs font-bold ${status.tone}`}>{status.label}</span>
          {order.status === 'ready' && (
            <button
              type="button"
              onClick={() => onMarkServed(order.id)}
              className={`min-h-[36px] px-3 rounded-full text-xs font-bold transition-colors ${v2 ? PRIMARY_V2 : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
            >
              {t('dineIn:served')}
            </button>
          )}
        </div>
        {showCourses ? (
          courses.map((course) => (
            <div key={course}>
              <div className="flex items-center gap-2 mt-2 -mb-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-t4">{COURSE_LABELS[course - 1] || course}</span>
                <span className="flex-1 h-px bg-line" />
              </div>
              <ul>{items.filter((i) => (i.course_number || 1) === course).map((i) => renderItem(i, cancelled))}</ul>
            </div>
          ))
        ) : (
          <ul className="mt-1">{items.map((i) => renderItem(i, cancelled))}</ul>
        )}
      </section>
    );
  };

  return (
    <div className="cashier-overlay fixed inset-0 bg-black/50 flex z-50 p-0">
      <div className="cashier-dialog bg-surface-1 w-full h-full overflow-hidden flex flex-col" style={{ borderRadius: 0 }}>
        {/* Κεφαλίδα: ποιο τραπέζι, σε τι κατάσταση, από πότε, ποιος */}
        <header className={`${v2 ? 'border-line' : 'cashier-dialog-header border-line-strong'} px-5 py-3 bg-surface-2 border-b flex items-center gap-4`}>
          <div className={`w-14 h-14 shrink-0 rounded-2xl flex items-center justify-center text-2xl font-bold tabular-nums ${v2 ? TONE_BADGE_V2[tone] : TONE_BADGE[tone]}`}>
            {table.table_number}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-bold text-t1 leading-tight">
                {t('dineIn:tableNumber', { number: table.table_number })}
                {table.name && <span className="font-medium text-t3"> · {table.name}</span>}
                {details.table?.zone_name && <span className="font-medium text-t3"> · {details.table.zone_name}</span>}
              </h2>
              {statusLabel && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface-3 text-xs font-bold text-t1">
                  <span className={`w-2 h-2 rounded-full ${v2 ? TONE_DOT_V2[tone] : TONE_DOT[tone]}`} />
                  {statusLabel}
                </span>
              )}
            </div>
            {session && (
              <div className="mt-1 flex items-center gap-4 text-sm text-t3 flex-wrap">
                {guestsEnabled && (
                  <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4" />{t('dineIn:guestsCount', { count: session.guest_count })}</span>
                )}
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  <span className="font-semibold text-t2 tabular-nums">{elapsed}</span>
                  <span className="tabular-nums">· {t('dineIn:tableScreen.since')} {time(session.opened_at)}</span>
                </span>
                {session.opened_by_name && (
                  <span className="inline-flex items-center gap-1.5 truncate"><User className="w-4 h-4" />{session.opened_by_name}</span>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common:close', { defaultValue: 'Κλείσιμο' })}
            className="w-11 h-11 shrink-0 rounded-xl hover:bg-surface-3 flex items-center justify-center transition-colors"
          >
            <X className="w-6 h-6 text-t3" />
          </button>
        </header>
        {reservation && (() => {
          const allergies = allergiesOf(reservation);
          return (
            <div className={`px-5 py-2.5 border-b flex items-center gap-x-3 gap-y-1.5 flex-wrap text-sm ${v2 ? 'bg-surface-1 border-line' : 'bg-blue-50 dark:bg-blue-900/20 border-line-strong'}`}>
              <CalendarDays className={`w-4 h-4 shrink-0 ${v2 ? 'text-t3' : 'text-blue-600 dark:text-blue-400'}`} />
              <span className="font-semibold text-t1">{reservation.customer_name}</span>
              <span className="text-t3 tabular-nums">
                {reservation.status === 'seated'
                  ? t('dineIn:sala.seatedReservation')
                  : t('dineIn:sala.reservationAt', { time: reservation.reservation_time.slice(0, 5) })}
                {' · '}{t('dineIn:guestsCount', { count: reservation.party_size })}
              </span>
              {reservation.guest_is_vip && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-bold">
                  <Star className="w-3 h-3" />VIP
                </span>
              )}
              {allergies && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-bold">
                  <AlertTriangle className="w-3 h-3" />{t('dineIn:sala.allergies', { text: allergies })}
                </span>
              )}
              {session?.deposit && session.deposit.amount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 text-xs font-bold">
                  <CreditCard className="w-3 h-3" />
                  {t(session.deposit.status === 'applied' ? 'dineIn:sala.depositApplied' : 'dineIn:sala.depositPending', { amount: `€${Number(session.deposit.amount).toFixed(2)}` })}
                </span>
              )}
              {reservation.notes && <span className="text-t3 truncate max-w-full">«{reservation.notes}»</span>}
            </div>
          );
        })()}

        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          {/* Τι έχει το τραπέζι */}
          <main className="flex-1 min-h-0 overflow-y-auto bg-surface-1 px-5">
            {session ? (
              <div className="max-w-4xl divide-y divide-line">
                {orders.map(renderOrder)}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-t4 py-16">
                <div className="text-center">
                  <Utensils className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">{t('dineIn:noActiveSession')}</p>
                  <p className="text-sm">{t('dineIn:noActiveSessionHint')}</p>
                </div>
              </div>
            )}
          </main>

          {/* Τι χρωστά και τι κάνω μετά */}
          <aside className={`w-full md:w-[25rem] shrink-0 bg-surface-2 border-t md:border-l flex flex-col min-h-0 overflow-y-auto ${
            v2 ? 'border-line md:border-t-[3px] md:border-t-brand' : 'md:border-t-0 border-line-strong'
          }`}>
            {session && (
              <section className="px-5 pt-4 pb-4 border-b border-line" aria-label={t('dineIn:bill')}>
                <p className={v2 ? 'text-sm font-medium text-t3' : 'text-xs font-bold uppercase tracking-wider text-t3'}>{t('dineIn:bill')}</p>
                <p className="mt-1 text-4xl font-bold text-t1 tabular-nums leading-none" data-testid="table-total">
                  {money(total)}
                </p>
                <p className="mt-1.5 text-sm text-t3">
                  {t('dineIn:tableScreen.itemCount', { count: itemCount })} · {t('dineIn:tableScreen.orderCount', { count: liveOrders.length })}
                </p>
                {discount > 0.005 && (
                  <dl className="mt-3 space-y-1 text-sm">
                    <div className="flex justify-between"><dt className="text-t3">{t('dineIn:tableScreen.subtotal')}</dt><dd className="tabular-nums text-t2">{money(subtotal)}</dd></div>
                    <div className="flex justify-between"><dt className="text-t3">{t('common:discount')}</dt><dd className={`tabular-nums font-semibold ${v2 ? 'text-t1' : 'text-emerald-600 dark:text-emerald-400'}`}>−{money(discount)}</dd></div>
                  </dl>
                )}
                {showPaid && (
                  <dl className="mt-3 rounded-xl bg-surface-1 border border-line p-3 space-y-1.5 text-sm" data-testid="table-paid">
                    <div className="flex justify-between"><dt className="text-t3">{t('dineIn:tableScreen.paidSoFar')}</dt><dd className={`tabular-nums font-semibold ${v2 ? 'text-t1' : 'text-emerald-600 dark:text-emerald-400'}`}>{money(payment.alreadyPaid)}</dd></div>
                    <div className="flex justify-between items-baseline"><dt className="font-bold text-t1">{t('dineIn:tableScreen.remaining')}</dt><dd className="text-xl font-bold tabular-nums text-t1">{money(payment.remainder)}</dd></div>
                  </dl>
                )}
                {lockReason && (
                  <p className="mt-3 flex items-start gap-2 text-[13px] leading-snug text-amber-800 dark:text-amber-300">
                    <Lock className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{lockReason}</span>
                  </p>
                )}
              </section>
            )}

            {/* Κύριες ενέργειες — με τη σειρά που τις χρειάζεται ο ταμίας */}
            <div className="px-5 pt-4 space-y-2">
              {canCollect && (
                <button
                  type="button"
                  onClick={onPayment}
                  className={`${v2 ? PRIMARY_V2 : 'cashier-primary bg-emerald-600 hover:bg-emerald-700 text-white'} w-full min-h-[56px] px-4 rounded-xl flex items-center gap-3 transition-all active:scale-[0.98]`}
                >
                  <CreditCard className="w-6 h-6 shrink-0" />
                  <span className="text-base font-bold">{t('dineIn:payment')}</span>
                  <span className="ml-auto text-lg font-bold tabular-nums">{money(payment.remainder)}</span>
                </button>
              )}
              {zeroTotal && can('dine_in_process_payment') && (
                <button
                  type="button"
                  onClick={onCloseZero}
                  disabled={busy.closeZero}
                  className={`${v2 ? PRIMARY_V2 : 'cashier-primary bg-emerald-600 hover:bg-emerald-700 text-white'} w-full min-h-[56px] px-4 rounded-xl flex items-center justify-center gap-2 font-bold transition-all active:scale-[0.98] disabled:opacity-50`}
                >
                  {busy.closeZero ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                  {t('dineIn:closeZeroTable', { defaultValue: 'Κλείσιμο τραπεζιού (0,00 €)' })}
                </button>
              )}
              {canNewOrder && (
                <button
                  type="button"
                  onClick={onNewOrder}
                  className={`w-full min-h-[56px] px-4 rounded-xl flex items-center justify-center gap-2 text-base font-bold transition-all active:scale-[0.98] ${
                    newOrderIsPrimary
                      ? (v2 ? PRIMARY_V2 : 'cashier-primary bg-blue-600 hover:bg-blue-700 text-white')
                      : (v2 ? 'bg-surface-2 border border-line-strong text-t1 hover:bg-surface-3' : 'bg-surface-1 border border-line-strong text-t1 hover:bg-surface-3')
                  }`}
                >
                  <Plus className="w-6 h-6" />
                  {t('dineIn:newOrder')}
                </button>
              )}
              {canReceipt && (
                <button
                  type="button"
                  onClick={onIssueReceipt}
                  disabled={busy.receipt}
                  className="w-full min-h-[52px] px-4 rounded-xl bg-surface-1 border-2 border-line-strong text-t1 hover:bg-surface-3 flex items-center justify-center gap-2 text-base font-bold transition-all active:scale-[0.98] disabled:opacity-50"
                >
                  {busy.receipt ? <Loader2 className="w-5 h-5 animate-spin" /> : <Receipt className="w-5 h-5" />}
                  {t('dineIn:issueReceipt')}
                </button>
              )}
              {(canBill || canInvoice) && (
                <div className="grid grid-cols-2 gap-2">
                  {canBill && (
                    <button
                      type="button"
                      onClick={onPrintBill}
                      disabled={busy.bill}
                      title={t('dineIn:printBill')}
                      className={`${canInvoice ? '' : 'col-span-2'} min-h-[48px] px-3 rounded-xl bg-surface-1 border border-line text-t2 hover:bg-surface-3 flex items-center justify-center gap-2 text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-50`}
                    >
                      {busy.bill ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                      {t('dineIn:tableScreen.billShort')}
                    </button>
                  )}
                  {canInvoice && (
                    <button
                      type="button"
                      onClick={onIssueInvoice}
                      title={t('dineIn:issueInvoice')}
                      className={`${canBill ? '' : 'col-span-2'} min-h-[48px] px-3 rounded-xl bg-surface-1 border border-line text-t2 hover:bg-surface-3 flex items-center justify-center gap-2 text-sm font-semibold transition-all active:scale-[0.98]`}
                    >
                      <FileText className="w-4 h-4" />
                      {t('dineIn:tableScreen.invoiceShort')}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Υπόλοιπες ενέργειες: ήσυχες, ίδιο σχήμα — το χρώμα μόνο στο εικονίδιο */}
            {sideActions.length > 0 && (
              <div className="px-5 pt-5">
                <p className={`${v2 ? 'text-sm font-medium' : 'text-xs font-bold uppercase tracking-wider'} text-t3 mb-2`}>{t('common:actions')}</p>
                <div className="grid grid-cols-2 gap-2">
                  {sideActions.map(({ key, ...a }) => <SideAction key={key} {...a} />)}
                </div>
              </div>
            )}

            {/* Καταστροφική ενέργεια: χώρια, στο τέλος, χωρίς γέμισμα */}
            {can('dine_in_cancel_order') && session && (
              <div className="px-5 pt-5 pb-5 mt-auto">
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={busy.cancel}
                  className="w-full min-h-[44px] px-3 rounded-xl border border-red-500/40 text-red-600 dark:text-red-400 hover:bg-red-500/10 flex items-center justify-center gap-2 text-sm font-semibold transition-colors disabled:opacity-50"
                >
                  {busy.cancel ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
                  {t('dineIn:tableScreen.cancelTable')}
                </button>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default TableScreen;
